import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaFilePdf, FaFileInvoiceDollar, FaTrash, FaSearch, FaCalendarAlt, FaTag, FaFileInvoice, FaTimes, FaSpinner, FaClock, FaCheckCircle, FaExclamationTriangle, FaUser, FaCar, FaSave, FaBan, FaBuilding, FaIdCard, FaReceipt } from 'react-icons/fa';
import { generaPdfPolizza } from './utils/generatePolizzaPdf.ts';
import { generaPdfAnnullamento } from './utils/generaPdfAnnullamento.ts'; // Importa la nuova funzione
import { generaPdfRisoluzione } from './utils/generaPdfRisoluzione.ts';
import Select from 'react-select';

export default function GestisciPolizzeAgenzia() {
  const navigate = useNavigate();

  const [userId, setUserId] = useState(null);
  const [agencyId, setAgencyId] = useState(null); // Nuovo stato per l'ID dell'agenzia
  const [agencyName, setAgencyName] = useState(''); // Nuovo stato per il nome dell'agenzia
  const [policies, setPolicies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
const [totalRows, setTotalRows] = useState(0);
const rowsPerPage = 25;


  // Stato per la tab attiva: 'attive' o 'eliminate'
  const [activeTab, setActiveTab] = useState('attive');

  const [draftFilters, setDraftFilters] = useState({
  cognome_cliente: '',
  ragione_sociale: '',
  codice_fiscale: '',
  partita_iva: '',
  tipologia_cliente: '',
  data_da: '',
  data_a: '',
  fine_decorrenza_da: '',
  fine_decorrenza_a: '',
  id_pacchetto_garanzia: '',
  stato_polizza: '',
  targa: '',
  tipologia_veicolo: '',
  nome_agente: ''
});

// ✅ questi sono i filtri “veri” usati nelle query
const [filters, setFilters] = useState(draftFilters);

  // Stati per le opzioni dei dropdown dei filtri
  const [pacchettiGaranziaOptions, setPacchettiGaranziaOptions] = useState([]);
  const [statoPolizzaOptions, setStatoPolizzaOptions] = useState([]);
  const [targaOptions, setTargaOptions] = useState([]);
  const [tipologiaVeicoloOptions, setTipologiaVeicoloOptions] = useState([]);
  const [agenteOptions, setAgenteOptions] = useState([]); // Nuovo stato per gli agenti dell'agenzia
  // Opzioni per il filtro Tipologia Cliente
  const [tipologiaClienteOptions] = useState([
    { value: 'PRIVATO', label: 'Privato' },
    { value: 'AZIENDA', label: 'Azienda / Libero Professionista' },
  ]);

  // Stati per la modale di conferma
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [modalAction, setModalAction] = useState(null);
  const [isDecisionModal, setIsDecisionModal] = useState(false); // Nuovo stato per distinguere modali a 1 o 2 bottoni
  const [downloadAnnullamentoId, setDownloadAnnullamentoId] = useState(null); // Nuovo stato per l'ID del PDF di annullamento

  // Stati per la modifica inline
  const [editingPolicyId, setEditingPolicyId] = useState(null);
  const [editedPolicyData, setEditedPolicyData] = useState(null);

  const baseCols = 14; // fino a "Stato"
const extraCols =
  (activeTab === 'attive' || activeTab === 'scadute') ? 2 /* Pacchetto + Data creazione */ :
  activeTab === 'eliminate' ? 1 /* Data eliminazione */ :
  activeTab === 'annullate' ? 2 /* Data annullamento + Premio restituito */ : 0;
const totalCols = baseCols + extraCols + 2; // + Note + Azioni

  // Funzione per aprire la modale
  const openConfirmModal = useCallback((title, message, actionCallback = null, decisionModal = false, annullamentoId = null) => {
    setModalTitle(title);
    setModalMessage(message);
    setModalAction(() => actionCallback);
    setIsDecisionModal(decisionModal); // Imposta il tipo di modale
    setDownloadAnnullamentoId(annullamentoId); // Salva l'ID per il download nel modale
    setShowConfirmModal(true);
  }, []);

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
    setModalAction(null);
    setIsDecisionModal(false); // Resetta il tipo di modale alla chiusura
    setDownloadAnnullamentoId(null); // Resetta l'ID
  };

  // --- Fetching Dati Iniziali (Utente, Agenzia, Opzioni Filtri, Adesioni) ---
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        if (!user) {
          openConfirmModal('Autenticazione Richiesta', 'Utente non autenticato. Sarai reindirizzato alla pagina di login.', () => navigate('/'));
          return;
        }
        setUserId(user.id);

        // Ottieni agency_id per l'utente corrente
        const { data: userData, error: userDataError } = await supabase
          .from('utenti')
          .select('agenzia_id')
          .eq('id', user.id)
          .single();

        if (userDataError) throw userDataError;
        if (!userData || !userData.agenzia_id) {
          openConfirmModal('Accesso Negato', 'Il tuo account non è associato ad alcuna agenzia. Contattare l\'amministratore.', () => navigate('/'));
          return;
        }
        setAgencyId(userData.agenzia_id);

        // Recupera il nome dell'agenzia usando agency_id
        const { data: agencyData, error: agencyDataError } = await supabase
          .from('agenzie')
          .select('nome')
          .eq('id', userData.agenzia_id)
          .single();
        
        if (agencyDataError) throw agencyDataError;
        setAgencyName(agencyData.nome);


        // Recupera gli agenti appartenenti a questa agenzia per il filtro
        const { data: agentsData, error: agentsError } = await supabase
            .from('utenti')
            .select('id, nome, cognome')
            .eq('agenzia_id', userData.agenzia_id)
            .eq('ruolo', 'agente'); // Filtra solo gli agenti
        if (agentsError) throw agentsError;
        setAgenteOptions(agentsData);

        // Recupera pacchetti garanzia per i filtri e per la modifica inline
        const { data: pacchettiData, error: pacchettiError } = await supabase
          .from('pacchetti_garanzia')
          .select('id, nome, valore_base, percentuale_netto') 
          .eq('attivo', true)
          .order('ordine', { ascending: true });
        if (pacchettiError) throw pacchettiError;
        setPacchettiGaranziaOptions(pacchettiData);

        // Stati possibili per il campo STATO (solo per EDIT)
if (activeTab === 'attive' || activeTab === 'scadute') {
  setStatoPolizzaOptions([
    { value: 'ATTIVA', label: 'Attiva' },
    { value: 'SCADUTA', label: 'Scaduta' },
  ]);
} else if (activeTab === 'eliminate') {
  setStatoPolizzaOptions([
    { value: 'ELIMINATA', label: 'Eliminata' },
  ]);
} else if (activeTab === 'annullate') {
  setStatoPolizzaOptions([
    { value: 'ANNULLATA', label: 'Annullata' },
  ]);
}

        // 🔹 Funzione generica per caricare tutte le targhe senza limite 1000
async function fetchAllRows(queryBuilder) {
  const allData = [];
  let from = 0;
  const limit = 1000;
  let more = true;

  while (more) {
    const { data, error } = await queryBuilder.range(from, from + limit - 1);
    if (error) throw error;
    if (!data || data.length === 0) {
      more = false;
    } else {
      allData.push(...data);
      from += limit;
      if (data.length < limit) more = false;
    }
  }
  return allData;
}

// 🔹 Recupera targhe ATTIVE per tutta l’agenzia
const targaPolizzeAttive = await fetchAllRows(
  supabase
    .from('polizze')
    .select('targa, id_agente')
    .in('id_agente', agentsData.map(agent => agent.id))
);

// 🔹 Recupera targhe ELIMINATE per tutta l’agenzia
const targaPolizzeEliminate = await fetchAllRows(
  supabase
    .from('polizze_eliminate')
    .select('targa, id_agente')
    .in('id_agente', agentsData.map(agent => agent.id))
);

        const allTargas = [
            ...targaPolizzeAttive.map(item => item.targa),
            ...targaPolizzeEliminate.map(item => item.targa)
        ].filter(Boolean); // Rimuove eventuali null/undefined
        const uniqueTargas = [...new Set(allTargas)].sort();
        setTargaOptions(uniqueTargas);

        // Definisci le opzioni per la tipologia veicolo
        setTipologiaVeicoloOptions([
          { value: 'Autovettura uso proprio', label: 'Autovettura uso proprio' },
          { value: 'Moto', label: 'Moto' },
          { value: 'Autocarro uso proprio', label: 'Autocarro uso proprio' },
        ]);

      } catch (err) {
        console.error('Errore nel caricamento dei dati iniziali:', err.message);
        openConfirmModal('Errore di Caricamento', `Impossibile caricare i dati iniziali: ${err.message}. Ricaricare la pagina o contattare l'assistenza.`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [navigate, openConfirmModal, activeTab]);


  // --- Funzioni di Fetching Dati (separate per chiarezza) ---
  const fetchActivePolicies = useCallback(async () => {
    if (!agencyId || agenteOptions.length === 0) return; // Attendi che agencyId e agentOptions siano impostati

    setIsLoading(true);
    try {
      // Ottieni tutti gli ID degli agenti appartenenti a questa agenzia
      const agentIdsInAgency = agenteOptions.map(agent => agent.id);

      let query = supabase
        .from('polizze') // Riferimento ancora a 'polizze' per la tabella del DB
        .select(`
          *,
          pacchetti_garanzia ( nome, valore_base, percentuale_netto ), 
          agenti:id_agente ( nome, cognome )
        `, { count: 'exact' })
        .in('id_agente', agentIdsInAgency);

        // 🔹 split per tab
if (activeTab === 'attive') {
  query = query.eq('stato_polizza', 'ATTIVA');
} else if (activeTab === 'scadute') {
  query = query.eq('stato_polizza', 'SCADUTA');
}

      // Applica filtri dinamici
      if (filters.cognome_cliente) {
        query = query.ilike('cognome_cliente', `%${filters.cognome_cliente}%`);
      }
      // ⭐ Nuovi filtri: Ragione Sociale, Codice Fiscale, Partita IVA, Tipologia Cliente ⭐
      if (filters.ragione_sociale) {
        query = query.ilike('ragione_sociale', `%${filters.ragione_sociale}%`);
      }
      if (filters.codice_fiscale) {
        query = query.ilike('codice_fiscale', `%${filters.codice_fiscale}%`);
      }
      if (filters.partita_iva) {
        query = query.ilike('partita_iva', `%${filters.partita_iva}%`);
      }
      if (filters.tipologia_cliente) {
        query = query.eq('cliente_privato', filters.tipologia_cliente === 'PRIVATO');
      }

      // ✅ Range creazione (created_at è timestamp)
const createdRange = normalizeRange(filters.data_da, filters.data_a);
if (createdRange.da) query = query.gte('created_at', createdRange.da);
if (createdRange.a) query = query.lt('created_at', toEndExclusiveISO(createdRange.a));

// ✅ Range fine decorrenza (di solito è "date" senza orario)
const fineRange = normalizeRange(filters.fine_decorrenza_da, filters.fine_decorrenza_a);
if (fineRange.da) query = query.gte('fine_decorrenza', fineRange.da);
if (fineRange.a) query = query.lte('fine_decorrenza', fineRange.a);

      if (filters.id_pacchetto_garanzia) {
        query = query.eq('id_pacchetto_garanzia', filters.id_pacchetto_garanzia);
      }
      if (filters.targa) {
        query = query.eq('targa', filters.targa);
      }
      if (filters.tipologia_veicolo) {
        query = query.eq('tipologia_veicolo', filters.tipologia_veicolo);
      }
      if (filters.nome_agente) { // Nuovo filtro per agente
        query = query.eq('id_agente', filters.nome_agente);
      }

      const { data, error, count } = await query
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
  .range((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage - 1);

if (error) throw error;

setPolicies(data);
setTotalRows(count ?? 0);

    } catch (err) {
      console.error('Errore nel recupero delle adesioni per l\'agenzia:', err.message);
      openConfirmModal('Errore nel recupero', `Errore nel recupero delle adesioni: ${err.message}`, null); 
      setPolicies([]);
    } finally {
      setIsLoading(false);
    }
  }, [agencyId, filters, openConfirmModal, agenteOptions, supabase, currentPage, rowsPerPage, activeTab]); // Aggiunto agenteOptions come dipendenza


  const fetchDeletedPolicies = useCallback(async () => {
    if (!agencyId || agenteOptions.length === 0) return;

    setIsLoading(true);
    try {
      const agentIdsInAgency = agenteOptions.map(agent => agent.id);

      let query = supabase
        .from('polizze_eliminate')
        .select(`
          *,
          pacchetti_garanzia ( percentuale_netto ),
          agenti:id_agente ( nome, cognome )
        `, { count: 'exact' })
        .in('id_agente', agentIdsInAgency);

      if (filters.cognome_cliente) {
        query = query.ilike('cognome_cliente', `%${filters.cognome_cliente}%`);
      }
      if (filters.ragione_sociale) {
        query = query.ilike('ragione_sociale', `%${filters.ragione_sociale}%`);
      }
      if (filters.codice_fiscale) {
        query = query.ilike('codice_fiscale', `%${filters.codice_fiscale}%`);
      }
      if (filters.partita_iva) {
        query = query.ilike('partita_iva', `%${filters.partita_iva}%`);
      }
      if (filters.tipologia_cliente) {
        query = query.eq('cliente_privato', filters.tipologia_cliente === 'PRIVATO');
      }

      // ✅ Range eliminazione (deleted_at è timestamp)
const deletedRange = normalizeRange(filters.data_da, filters.data_a);
if (deletedRange.da) query = query.gte('deleted_at', deletedRange.da);
if (deletedRange.a) query = query.lt('deleted_at', toEndExclusiveISO(deletedRange.a));

      if (filters.targa) {
        query = query.eq('targa', filters.targa);
      }
      if (filters.tipologia_veicolo) {
        query = query.eq('tipologia_veicolo', filters.tipologia_veicolo);
      }
      if (filters.nome_agente) {
        query = query.eq('id_agente', filters.nome_agente);
      }

      const { data, error, count } = await query
      .order('deleted_at', { ascending: false })
      .order('id', { ascending: false })
  .range((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage - 1);

if (error) throw error;

setPolicies(data);
setTotalRows(count ?? 0);

    } catch (err) {
      console.error('Errore nel recupero delle polizze eliminate per l\'agenzia:', err.message);
      openConfirmModal('Errore nel recupero', `Errore nel recupero delle polizze eliminate: ${err.message}`, null);
      setPolicies([]);
    } finally {
      setIsLoading(false);
    }
  }, [agencyId, filters, openConfirmModal, agenteOptions, supabase, currentPage, rowsPerPage, activeTab]);

  const fetchAnnullatePolicies = useCallback(async () => {
  if (!agencyId || agenteOptions.length === 0) return;
  setIsLoading(true);
  try {
    const agentIdsInAgency = agenteOptions.map(a => a.id);
    let query = supabase
      .from('polizze')
      .select(`
        *,
        pacchetti_garanzia ( nome, percentuale_netto ),
        agenti:id_agente ( nome, cognome )
      `, { count: 'exact' })
      .in('id_agente', agentIdsInAgency)
      .eq('stato_polizza', 'ANNULLATA');

    if (filters.cognome_cliente) query = query.ilike('cognome_cliente', `%${filters.cognome_cliente}%`);
    if (filters.ragione_sociale) query = query.ilike('ragione_sociale', `%${filters.ragione_sociale}%`);
    if (filters.tipologia_cliente) {
  query = query.eq('cliente_privato', filters.tipologia_cliente === 'PRIVATO');
}
if (filters.nome_agente) query = query.eq('id_agente', filters.nome_agente);
if (filters.tipologia_veicolo) query = query.eq('tipologia_veicolo', filters.tipologia_veicolo);
if (filters.agenzia_id_filter) {
  const { data: agentsInAgency } = await supabase
    .from('utenti').select('id').eq('agenzia_id', filters.agenzia_id_filter);
  const ids = (agentsInAgency || []).map(a => a.id);
  if (ids.length) query = query.in('id_agente', ids); else { setPolicies([]); setIsLoading(false); return; }
}
// se vuoi anche range per l’annullamento (se hai una data dedicata es. data_risoluzione):
// ✅ Range risoluzione (se data_risoluzione è timestamp)
const risRange = normalizeRange(filters.data_da, filters.data_a);
if (risRange.da) query = query.gte('data_risoluzione', risRange.da);
if (risRange.a) query = query.lt('data_risoluzione', toEndExclusiveISO(risRange.a));


    if (filters.targa) query = query.eq('targa', filters.targa);
    if (filters.tipologia_veicolo) query = query.eq('tipologia_veicolo', filters.tipologia_veicolo);
    if (filters.nome_agente) query = query.eq('id_agente', filters.nome_agente);

    const { data, error, count } = await query
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
  .range((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage - 1);

if (error) throw error;

setPolicies(data);
setTotalRows(count ?? 0);
  } catch (err) {
    openConfirmModal('Errore', `Impossibile caricare le adesioni annullate: ${err.message}`);
    setPolicies([]);
  } finally {
    setIsLoading(false);
  }
}, [agencyId, agenteOptions, filters, openConfirmModal, supabase, currentPage, rowsPerPage, activeTab]);


  // Esegui fetchPolicies/fetchDeletedPolicies quando agencyId, agentOptions, i filtri o la tab cambiano
useEffect(() => {
  if (activeTab === 'attive' || activeTab === 'scadute') {
    fetchActivePolicies();
  } else if (activeTab === 'eliminate') {
    fetchDeletedPolicies();
  } else if (activeTab === 'annullate') {
    fetchAnnullatePolicies();
  }
}, [activeTab, currentPage, fetchActivePolicies, fetchDeletedPolicies, fetchAnnullatePolicies]);

useEffect(() => {
  setCurrentPage(1); // torna sempre a pagina 1 quando cambia un filtro
}, [filters, activeTab]);

const isISODate = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
const safeISODateOrEmpty = (s) => (isISODate(s) ? s : '');

const normalizeRange = (da, a) => {
  const d1 = safeISODateOrEmpty(da);
  const d2 = safeISODateOrEmpty(a);
  if (!d1 || !d2) return { da: d1, a: d2 };
  return d1 <= d2 ? { da: d1, a: d2 } : { da: d2, a: d1 };
};

const DATE_FIELDS = new Set(['data_da','data_a','fine_decorrenza_da','fine_decorrenza_a']);

const handleFilterChange = (e) => {
  const { name, value } = e.target;

  // 1) aggiorna SEMPRE la UI
  setDraftFilters(prev => ({ ...prev, [name]: value }));

  // 2) se NON è una data, applica subito (comportamento attuale)
  if (!DATE_FIELDS.has(name)) {
    setFilters(prev => ({ ...prev, [name]: value }));
  }
};

const commitDateFilters = () => {
  setFilters(prev => {
    // prendi le date dal draft
    const created = normalizeRange(draftFilters.data_da, draftFilters.data_a);
    const fine = normalizeRange(draftFilters.fine_decorrenza_da, draftFilters.fine_decorrenza_a);

    return {
      ...prev,
      data_da: created.da,
      data_a: created.a,
      fine_decorrenza_da: fine.da,
      fine_decorrenza_a: fine.a,
    };
  });
};

const handleDateKeyDown = (e) => {
  if (e.key === 'Enter') {
    e.currentTarget.blur();   // forza blur
    commitDateFilters();      // applica
  }
};

const handleClearFilters = () => {
  const cleared = {
    cognome_cliente: '',
    ragione_sociale: '',
    codice_fiscale: '',
    partita_iva: '',
    tipologia_cliente: '',
    data_da: '',
    data_a: '',
    fine_decorrenza_da: '',
    fine_decorrenza_a: '',
    id_pacchetto_garanzia: '',
    stato_polizza: '',
    targa: '',
    tipologia_veicolo: '',
    nome_agente: ''
  };

  setDraftFilters(cleared);
  setFilters(cleared);
};

// ✅ Utility per "fine giornata" (inclusivo) su campi timestamp
const toEndExclusiveISO = (yyyy_mm_dd) => {
  // yyyy_mm_dd è già validato
  const d = new Date(yyyy_mm_dd);
  d.setDate(d.getDate() + 1); // giorno successivo
  return d.toISOString();
};

  // Logica per abilitare EDIT / ELIMINA
  // Per l'agenzia, le adesioni possono essere modificate/eliminate se create oggi da QUALSIASI agente dell'agenzia
  const canEditPolicy = useCallback((policyCreatedAt) => {
    const createdDate = new Date(policyCreatedAt);
    const now = new Date();

    const createdDateRome = new Date(createdDate.toLocaleString('en-US', { timeZone: 'Europe/Rome' }));
    const nowRome = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Rome' }));
    
    createdDateRome.setHours(0, 0, 0, 0);
    nowRome.setHours(0, 0, 0, 0);

    // Se la data di creazione è uguale alla data corrente, si può modificare.
    // Altrimenti (se è un giorno precedente), non si può modificare.
    return createdDateRome.getTime() === nowRome.getTime();
  }, []);

  // ⭐ Funzione per il calcolo preciso dei mesi (copiata da NuovaPolizza.js per coerenza) ⭐
  const calculateMonthsPrecise = (startDateStr, endDateStr) => {
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()) || startDate > endDate) {
        return 0; // Se le date non sono valide o inizio > fine, restituisce 0
    }

    const sYear = startDate.getFullYear();
    const sMonth = startDate.getMonth(); // 0-indexed
    const sDay = startDate.getDate();

    const eYear = endDate.getFullYear();
    const eMonth = endDate.getMonth(); // 0-indexed
    const eDay = endDate.getDate();

    // Calcola la differenza di mesi completa (ignorando il giorno per ora)
    let months = (eYear - sYear) * 12 + (eMonth - sMonth);

    // Regola l'arrotondamento in base al giorno di inizio e fine
    // Se il giorno di fine è minore del giorno di inizio, significa che
    // l'anniversario del mese non è ancora stato raggiunto, quindi sottraiamo un mese.
    if (endDate.getTime() <= startDate.getTime() || (eDay <= sDay && !(eYear === sYear && eMonth === sMonth && eDay === sDay + 1))) {
        months--;
    }

    // Se `months` è 0 (es. dal 25 Giugno al 24 Luglio), significa che non è passato un mese intero per anniversario.
    // Per la tua regola "al 25 scatta il mese successivo" e arrotondamento per eccesso,
    // se le date non sono uguali e c'è una durata, conta almeno 1 mese.
    if (months === 0 && (endDate.getTime() !== startDate.getTime())) {
        return 1;
    }
    
    // Per durate superiori o uguali a un mese "anniversario", si aggiunge 1 per l'arrotondamento per eccesso
    // (al 25 scatta il mese successivo, quindi un mese esatto conta come due mesi effettivi se la logica è "al mese successivo")
    // Sembra che la tua logica volesse che anche 1 mese esatto (es. 25/06 - 24/07) venisse considerato come 2 mesi per il calcolo.
    // Se l'intento è "frazioni di mese contano come mese intero", allora `months + 1` funziona per tutti i casi > 0 giorni.
    if (months >= 0 && (endDate.getTime() > startDate.getTime())) {
      return months + 1;
    } else if (endDate.getTime() === startDate.getTime()) {
      return 0; // Se inizio e fine sono lo stesso giorno, la durata è 0
    }
    
    return months; // Per altri casi, es. date invalide già catturate all'inizio
  };


  // --- Gestione Azioni ---
  // ⭐ Funzione per attivare la modalità di modifica ⭐
  const handleEditClick = (policy) => {
    setEditingPolicyId(policy.id);
    // Copia tutti i dati della policy per la modifica, inclusi i nuovi campi
    setEditedPolicyData({ ...policy }); 
  };

  // ⭐ Gestione dei cambiamenti nei campi modificabili ⭐
  const handleEditChange = (e) => {
    const { name, value, type } = e.target;
  
    setEditedPolicyData(prev => {
      let newState = { ...prev, [name]: type === 'number' ? parseFloat(value) || 0 : value };
  
      // Se cambiano le date di decorrenza o il pacchetto garanzia, ricalcola il valore adesione
      if (name === 'inizio_decorrenza' || name === 'fine_decorrenza' || name === 'id_pacchetto_garanzia') {
        const currentStartDate = newState.inizio_decorrenza;
        const currentEndDate = newState.fine_decorrenza;
        const currentPackageId = newState.id_pacchetto_garanzia;
  
        const selectedPackage = pacchettiGaranziaOptions.find(p => p.id === currentPackageId);
        
        if (selectedPackage && currentStartDate && currentEndDate) {
          const baseValue = parseFloat(selectedPackage.valore_base);
          // ⭐ Calcolo dei mesi effettivi usando la funzione unificata ⭐
          let effectiveMonths = calculateMonthsPrecise(currentStartDate, currentEndDate);
  
          // Assicurati che il minimo sia 1 mese per una polizza valida se la durata è > 0 giorni
          if (effectiveMonths < 1 && (new Date(currentEndDate).getTime() > new Date(currentStartDate).getTime())) {
            effectiveMonths = 1;
          }
          
          const monthlyBase = baseValue / 12; // quota mensile
          // ⭐ Applica la logica di calcolo del premio (limite a 14 mesi) ⭐
          const calculatedValue = monthlyBase * Math.min(effectiveMonths, 14);
          
          newState.valore_polizza_calcolato = parseFloat(calculatedValue.toFixed(2));
        } else {
          newState.valore_polizza_calcolato = 0;
        }
      }
      return newState;
    });
  };

  // ⭐ Funzione per salvare le modifiche e loggare ⭐
  const handleSaveEdit = useCallback(async (policyId) => {
    setIsLoading(true);
    try {
      const originalPolicy = policies.find(p => p.id === policyId);
      if (!originalPolicy) {
        throw new Error('Adesione originale non trovata per il logging.');
      }

      // Prepara i dati per il log, escludendo il campo `pacchetti_garanzia` che è un oggetto annidato non direttamente modificabile
      const oldDataForLog = { ...originalPolicy };
      delete oldDataForLog.pacchetti_garanzia; 
      
      const newDataForLog = { ...editedPolicyData };
      delete newDataForLog.pacchetti_garanzia;

      // Confronta solo i campi modificabili per vedere se ci sono state modifiche effettive
      const fieldsToCompare = [
        'tipo_operazione', 'nome_cliente', 'cognome_cliente', 'ragione_sociale', 'codice_fiscale', 'partita_iva', // Inclusi i nuovi campi
        'tipologia_veicolo', 'modello_auto', 'data_immatricolazione', 'targa', 'inizio_decorrenza',
        'fine_decorrenza', 'id_pacchetto_garanzia', 'valore_polizza_calcolato', 'stato_polizza', 'note'
      ];

      let hasChanges = false;
      for (const field of fieldsToCompare) {
        // Conversione esplicita a stringa per confronto affidabile
        const oldValue = originalPolicy[field] !== null && originalPolicy[field] !== undefined ? String(originalPolicy[field]) : '';
        const newValue = editedPolicyData[field] !== null && editedPolicyData[field] !== undefined ? String(editedPolicyData[field]) : '';
        
        if (oldValue !== newValue) {
          hasChanges = true;
          break;
        }
      }

      if (hasChanges) {
        // Logga la modifica
        const { error: logError } = await supabase
          .from('log_edit_polizze')
          .insert({
            policy_id: policyId,
            editor_id: userId,
            description: `Modifica dati adesione N. ${originalPolicy.numero_polizza || policyId}`,
            old_data: oldDataForLog, // Oggetto JSON dei dati prima
            new_data: newDataForLog  // Oggetto JSON dei dati dopo
          });

        if (logError) {
          console.error('Errore durante il logging della modifica:', logError.message);
          openConfirmModal('Errore di Logging', `L'adesione è stata aggiornata, ma si è verificato un errore durante la registrazione del log: ${logError.message}. Contattare l'assistenza.`, null);
          // Non bloccare l'aggiornamento della polizza se il log fallisce, potrebbe essere un errore temporaneo
        }

        // Aggiorna l'adesione nel database, includendo i nuovi campi
        const { error: updateError } = await supabase
          .from('polizze') // Riferimento ancora a 'polizze' per la tabella del DB
          .update({
            tipo_operazione: editedPolicyData.tipo_operazione,
            nome_cliente: editedPolicyData.nome_cliente,
            cognome_cliente: editedPolicyData.cognome_cliente,
            ragione_sociale: editedPolicyData.ragione_sociale, // Nuovo campo
            codice_fiscale: editedPolicyData.codice_fiscale,   // Nuovo campo
            partita_iva: editedPolicyData.partita_iva,         // Nuovo campo
            cliente_privato: editedPolicyData.cliente_privato, // Nuovo campo
            tipologia_veicolo: editedPolicyData.tipologia_veicolo,
            modello_auto: editedPolicyData.modello_auto,
            data_immatricolazione: editedPolicyData.data_immatricolazione,
            targa: editedPolicyData.targa,
            inizio_decorrenza: editedPolicyData.inizio_decorrenza,
            fine_decorrenza: editedPolicyData.fine_decorrenza,
            id_pacchetto_garanzia: editedPolicyData.id_pacchetto_garanzia,
            valore_polizza_calcolato: editedPolicyData.valore_polizza_calcolato,
            stato_polizza: editedPolicyData.stato_polizza, // Lo stato può essere modificato
            note: editedPolicyData.note
          })
          .eq('id', policyId);

        if (updateError) {
          throw updateError;
        }

        openConfirmModal('Successo!', 'Adesione aggiornata e log registrato con successo.', null); // ⭐ Usa la modale per il successo ⭐
        setEditingPolicyId(null); // Esce dalla modalità di modifica
        setEditedPolicyData(null);
        fetchActivePolicies(); // Ricarica le adesioni per mostrare i dati aggiornati

      } else {
        openConfirmModal('Attenzione', 'Nessuna modifica rilevata. L\'adesione non è stata aggiornata.', null); // ⭐ Usa la modale per nessuna modifica ⭐
        setEditingPolicyId(null); // Esce dalla modalità di modifica
        setEditedPolicyData(null);
      }

    } catch (err) {
      console.error('Errore durante l\'aggiornamento dell\'adesione:', err.message);
      openConfirmModal('Errore', `Errore nell'aggiornamento dell'adesione: ${err.message}.`, null); // ⭐ Usa la modale per l'errore ⭐
    } finally {
      setIsLoading(false);
    }
  }, [policies, editedPolicyData, userId, openConfirmModal, fetchActivePolicies, pacchettiGaranziaOptions]); // Added pacchettiGaranziaOptions to dependencies for calculateMonthsPrecise

  // ⭐ Funzione per annullare la modifica ⭐
  const handleCancelEdit = () => {
    setEditingPolicyId(null);
    setEditedPolicyData(null);
  };


 const handleDownloadPdf = async (policy) => {
   try {
     const blob = await generaPdfPolizza(policy.id);
     const url = URL.createObjectURL(blob);
     const a = document.createElement('a');
     a.href = url;
     a.download = `polizza_${policy.numero_polizza ?? policy.id}.pdf`;
     a.click();
     URL.revokeObjectURL(url);
   } catch (err) {
     openConfirmModal('Errore PDF', `Impossibile generare il PDF della polizza: ${err.message}.`);
   }
 };

  // ⭐ NUOVA FUNZIONE: Scarica PDF Annullamento ⭐
  const handleDownloadAnnullamentoPdf = async (policyId) => {
    try {
      const blob = await generaPdfAnnullamento(policyId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `annullamento_${policyId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      closeConfirmModal(); // Chiude la modale dopo il download
    } catch (err) {
      openConfirmModal('Errore PDF Annullamento', `Impossibile generare il PDF di annullamento: ${err.message}.`);
    }
  };

  // ⭐ NUOVA FUNZIONE: Scarica PDF Risoluzione ⭐
const handleDownloadRisoluzionePdf = async (policyId) => {
  try {
    const blob = await generaPdfRisoluzione(policyId);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `risoluzione_${policyId}.pdf`; // 🔹 Nome file chiaro
    a.click();
    URL.revokeObjectURL(url);
    closeConfirmModal(); // Chiudi eventuale modale dopo il download
  } catch (err) {
    openConfirmModal(
      'Errore PDF Risoluzione',
      `Impossibile generare il PDF di risoluzione: ${err.message}.`
    );
  }
};

  // ⭐ NUOVA FUNZIONE: Eliminazione definitiva della adesione (con pop-up di download) ⭐
  const handleDeletePolicy = useCallback(async (policyId, policyNumber) => {
    openConfirmModal(
      'Conferma Eliminazione Definitiva',
      `Sei sicuro di voler eliminare DEFINITIVAMENTE l'adesione N. ${policyNumber || policyId}? Questa azione è irreversibile. Ti ricordiamo che l'eliminazione va effettuata solo in caso di TARGA ERRATA.`,
      async () => {
        setIsLoading(true);
        try {
          // 1. Retrieve the policy to be deleted
          const { data: policyToMove, error: fetchError } = await supabase
            .from('polizze')
            .select('*')
            .eq('id', policyId)
            .single();

          if (fetchError) throw fetchError;

          // 2. Insert the policy into the 'polizze_eliminate' table
          const { error: insertError } = await supabase
            .from('polizze_eliminate')
            .insert({
              id: policyToMove.id, // Keep the same ID
              id_agente: policyToMove.id_agente,
              nome_cliente: policyToMove.nome_cliente,
              cognome_cliente: policyToMove.cognome_cliente,
              // ⭐ Nuovi campi da copiare ⭐
              cliente_privato: policyToMove.cliente_privato,
              ragione_sociale: policyToMove.ragione_sociale,
              codice_fiscale: policyToMove.codice_fiscale,
              partita_iva: policyToMove.partita_iva,
              // Fine nuovi campi
              tipologia_veicolo: policyToMove.tipologia_veicolo,
              modello_auto: policyToMove.modello_auto,
              data_immatricolazione: policyToMove.data_immatricolazione,
              targa: policyToMove.targa,
              inizio_decorrenza: policyToMove.inizio_decorrenza,
              fine_decorrenza: policyToMove.fine_decorrenza,
              id_pacchetto_garanzia: policyToMove.id_pacchetto_garanzia,
              valore_polizza_calcolato: policyToMove.valore_polizza_calcolato,
              stato_polizza: 'ELIMINATA', // Specific status for deleted policies
              note: policyToMove.note,
              numero_polizza: policyToMove.numero_polizza
              // 'deleted_at' will be automatically populated by the database default (now())
            });

          if (insertError) {
            console.error('Errore durante l\'inserimento in polizze_eliminate:', insertError.message);
            throw new Error(`Errore durante l'archiviazione dell'adesione eliminata: ${insertError.message}`);
          }

          // 3. Delete the policy from the 'polizze' table
          const { error: deleteError } = await supabase
            .from('polizze')
            .delete()
            .eq('id', policyId);

          if (deleteError) {
            // If deletion fails after insertion into _eliminate, try to revert insertion or log more thoroughly
            console.error('Errore durante l\'eliminazione da polizze dopo l\'inserimento in polizze_eliminate:', deleteError.message);
            throw new Error(`Adesione archiviata, ma errore durante l'eliminazione dalla tabella principale: ${deleteError.message}`);
          }

          // Passa l'ID della polizza appena eliminata alla modale di successo
          openConfirmModal('Successo!', `Adesione N. ${policyNumber || policyId} eliminata definitivamente e archiviata! Vuoi scaricare la ricevuta di annullamento?`, null, false, policyId);
          fetchActivePolicies(); // Re-fetch policies to update the list
        } catch (err) {
          console.error('Errore durante l\'eliminazione dell\'adesione:', err.message);
          openConfirmModal('Errore', `Errore nell'eliminazione definitiva dell'adesione: ${err.message}.`, null);
        } finally {
          setIsLoading(false);
        }
      },
      true // This parameter indicates that it is a decision modal (Yes/No)
    );
  }, [openConfirmModal, fetchActivePolicies]);


  if (isLoading && policies.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 text-slate-800">
        <FaSpinner className="animate-spin text-4xl text-mioblu mr-3" />
        <p className="text-xl font-semibold">Caricamento dashboard Agenzia...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-7xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold uppercase mb-6 text-center text-mioblu">
          Gestione Adesioni {agencyName}
        </h1>

        {/* Tab Buttons */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            className={`px-4 py-2 text-sm font-medium ${activeTab === 'attive' ? 'border-b-2 border-miorosso text-miorosso' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => {
              setActiveTab('attive');
              setFilters(prev => ({ // Reset filtri rilevanti per la tab
                ...prev,
                data_da: '', data_a: '',
                stato_polizza: '' // Reset stato per evitare problemi tra le tab
              }));
              setStatoPolizzaOptions([ // Aggiorna le opzioni di stato
                { value: 'ATTIVA', label: 'Attiva' },
                { value: 'SCADUTA', label: 'Scaduta' },
              ]);
            }}
          >
            Adesioni Attive
          </button>
          <button
  className={`ml-4 px-4 py-2 text-sm font-medium ${
    activeTab === 'scadute'
      ? 'border-b-2 border-miorosso text-miorosso'
      : 'text-gray-500 hover:text-gray-700'
  }`}
  onClick={() => {
    setActiveTab('scadute');
    setFilters(prev => ({
      ...prev,
      data_da: '',
      data_a: '',
      // posso anche lasciare i filtri fine_decorrenza, che hanno senso per scadute
      // stato_polizza non ci interessa più come filtro
    }));
    setStatoPolizzaOptions([
      { value: 'ATTIVA', label: 'Attiva' },
      { value: 'SCADUTA', label: 'Scaduta' },
    ]);
  }}
>
  Adesioni Scadute
</button>

          <button
            className={`ml-4 px-4 py-2 text-sm font-medium ${activeTab === 'eliminate' ? 'border-b-2 border-miorosso text-miorosso' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => {
              setActiveTab('eliminate');
              setFilters(prev => ({ // Reset filtri rilevanti per la tab
                ...prev,
                data_da: '', data_a: '', // Per data eliminazione
                fine_decorrenza_da: '', fine_decorrenza_a: '', // Non applicabili
                id_pacchetto_garanzia: '', // Non applicabile
                stato_polizza: 'ELIMINATA' // Imposta fisso lo stato
              }));
              setStatoPolizzaOptions([ // Aggiorna le opzioni di stato
                { value: 'ELIMINATA', label: 'Eliminata' },
              ]);
            }}
          >
            Adesioni Eliminate
          </button>
          {/* Annullate */}
  <button
  className={`ml-4 px-4 py-2 text-sm font-medium ${activeTab === 'annullate' ? 'border-b-2 border-miorosso text-miorosso' : 'text-gray-500 hover:text-gray-700'}`}
  onClick={() => {
    setActiveTab('annullate');
    setFilters(prev => ({
      ...prev,
      data_da: '', data_a: '',
      fine_decorrenza_da: '', fine_decorrenza_a: '',
      id_pacchetto_garanzia: '',
      stato_polizza: 'ANNULLATA'
    }));
    setStatoPolizzaOptions([
      { value: 'ANNULLATA', label: 'Annullata' }
    ]);
  }}
>
  Adesioni Annullate
</button>

        </div>


        {/* Sezione Filtri */}
        <div className="mb-6 p-4 bg-gray-100 rounded-lg shadow-inner">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Filtri Adesioni</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            
            {/* Nuovo filtro Tipologia Cliente (sempre visibile) */}
            <div>
              <label htmlFor="tipologia_cliente" className="block text-sm font-medium text-gray-700">Tipologia Cliente</label>
              <div className="relative mt-1">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3"><FaUser className="text-gray-400" /></span>
                <select
                  id="tipologia_cliente"
                  name="tipologia_cliente"
                  value={filters.tipologia_cliente}
                  onChange={handleFilterChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mioblu focus:border-mioblu sm:text-sm appearance-none bg-white"
                >
                  <option value="">Tutti</option>
                  {tipologiaClienteOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Campi filtro condizionali in base alla tipologia cliente */}
            {filters.tipologia_cliente === 'PRIVATO' && (
              <>
                {/* Cognome Cliente */}
                <div>
                  <label htmlFor="cognome_cliente" className="block text-sm font-medium text-gray-700">Cognome Cliente</label>
                  <div className="relative mt-1">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3"><FaUser className="text-gray-400" /></span>
                    <input
                      type="text"
                      id="cognome_cliente"
                      name="cognome_cliente"
                      value={filters.cognome_cliente}
                      onChange={handleFilterChange}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mioblu focus:border-mioblu sm:text-sm"
                      placeholder="Es. Rossi"
                    />
                  </div>
                </div>

                {/* Codice Fiscale */}
                <div>
                  <label htmlFor="codice_fiscale" className="block text-sm font-medium text-gray-700">Codice Fiscale</label>
                  <div className="relative mt-1">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3"><FaIdCard className="text-gray-400" /></span>
                    <input
                      type="text"
                      id="codice_fiscale"
                      name="codice_fiscale"
                      value={filters.codice_fiscale}
                      onChange={handleFilterChange}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mioblu focus:border-mioblu sm:text-sm"
                      placeholder="Es. ABCDEF12G34H567I"
                    />
                  </div>
                </div>
              </>
            )}

            {filters.tipologia_cliente === 'AZIENDA' && (
              <>
                {/* Ragione Sociale */}
                <div>
                  <label htmlFor="ragione_sociale" className="block text-sm font-medium text-gray-700">Ragione Sociale</label>
                  <div className="relative mt-1">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3"><FaBuilding className="text-gray-400" /></span>
                    <input
                      type="text"
                      id="ragione_sociale"
                      name="ragione_sociale"
                      value={filters.ragione_sociale}
                      onChange={handleFilterChange}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mioblu focus:border-mioblu sm:text-sm"
                      placeholder="Es. Alfa S.p.A."
                    />
                  </div>
                </div>

                {/* Partita IVA */}
                <div>
                  <label htmlFor="partita_iva" className="block text-sm font-medium text-gray-700">Partita IVA</label>
                  <div className="relative mt-1">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3"><FaReceipt className="text-gray-400" /></span>
                    <input
                      type="text"
                      id="partita_iva"
                      name="partita_iva"
                      value={filters.partita_iva}
                      onChange={handleFilterChange}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mioblu focus:border-mioblu sm:text-sm"
                      placeholder="Es. 12345678901"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Data Creazione / Eliminazione (Da) */}
<div>
  <label htmlFor="data_da">
    {(activeTab === 'attive' || activeTab === 'scadute') 
      ? 'Creazione (Da)' 
      : activeTab === 'eliminate' 
        ? 'Eliminazione (Da)' 
        : 'Risoluzione (Da)'}
  </label>

  <div className="relative mt-1">
    <span className="absolute inset-y-0 left-0 flex items-center pl-3">
      <FaCalendarAlt className="text-gray-400" />
    </span>
    <input
      type="date"
      id="data_da"
      name="data_da"
      value={draftFilters.data_da}
  onChange={handleFilterChange}
  onBlur={commitDateFilters}
  onKeyDown={handleDateKeyDown}
      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mioblu focus:border-mioblu sm:text-sm"
    />
  </div>
</div>

{/* Data Creazione / Eliminazione (A) */}
<div>
  <label htmlFor="data_a">
    {(activeTab === 'attive' || activeTab === 'scadute') 
      ? 'Creazione (A)' 
      : activeTab === 'eliminate' 
        ? 'Eliminazione (A)' 
        : 'Risoluzione (A)'}
  </label>

  <div className="relative mt-1">
    <span className="absolute inset-y-0 left-0 flex items-center pl-3">
      <FaCalendarAlt className="text-gray-400" />
    </span>
    <input
      type="date"
      id="data_a"
      name="data_a"
      value={draftFilters.data_a}
  onChange={handleFilterChange}
  onBlur={commitDateFilters}
  onKeyDown={handleDateKeyDown}
      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mioblu focus:border-mioblu sm:text-sm"
    />
  </div>
</div>


            {(activeTab === 'attive' || activeTab === 'scadute') && (
  <>
    {/* Data Fine Decorrenza (Da) */}
    <div>
      <label htmlFor="fine_decorrenza_da" className="block text-sm font-medium text-gray-700">Fine Decorrenza (Da)</label>
      <div className="relative mt-1">
        <span className="absolute inset-y-0 left-0 flex items-center pl-3">
          <FaCalendarAlt className="text-gray-400" />
        </span>
        <input
          type="date"
          id="fine_decorrenza_da"
          name="fine_decorrenza_da"
          value={draftFilters.fine_decorrenza_da}
  onChange={handleFilterChange}
  onBlur={commitDateFilters}
  onKeyDown={handleDateKeyDown}
          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mioblu focus:border-mioblu sm:text-sm"
        />
      </div>
    </div>

    {/* Data Fine Decorrenza (A) */}
    <div>
      <label htmlFor="fine_decorrenza_a" className="block text-sm font-medium text-gray-700">Fine Decorrenza (A)</label>
      <div className="relative mt-1">
        <span className="absolute inset-y-0 left-0 flex items-center pl-3">
          <FaCalendarAlt className="text-gray-400" />
        </span>
        <input
          type="date"
          id="fine_decorrenza_a"
          name="fine_decorrenza_a"
          value={draftFilters.fine_decorrenza_a}
  onChange={handleFilterChange}
  onBlur={commitDateFilters}
  onKeyDown={handleDateKeyDown}
          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mioblu focus:border-mioblu sm:text-sm"
        />
      </div>
    </div>

    {/* Pacchetto Garanzia */}
    <div>
      <label htmlFor="id_pacchetto_garanzia" className="block text-sm font-medium text-gray-700">Pacchetto Garanzia</label>
      <div className="relative mt-1">
        <span className="absolute inset-y-0 left-0 flex items-center pl-3">
          <FaFileInvoice className="text-gray-400" />
        </span>
        <select
          id="id_pacchetto_garanzia"
          name="id_pacchetto_garanzia"
          value={filters.id_pacchetto_garanzia}
          onChange={handleFilterChange}
          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mioblu focus:border-mioblu sm:text-sm appearance-none bg-white"
        >
          <option value="">Tutti</option>
          {pacchettiGaranziaOptions.map(pkg => (
            <option key={pkg.id} value={pkg.id}>{pkg.nome}</option>
          ))}
        </select>
      </div>
    </div>
  </>
)}


            {/* New filter for License Plate with autocomplete */}
<div>
  <label className="block text-sm font-medium text-gray-700">Targa</label>
  <Select
    options={targaOptions.map(t => ({ value: t, label: t }))}
    value={filters.targa ? { value: filters.targa, label: filters.targa } : null}
    onChange={(option) => setFilters(prev => ({ ...prev, targa: option?.value || '' }))}
    isClearable
    placeholder="Seleziona o cerca targa..."
    className="mt-1"
  />
</div>


            {/* Nuovo filtro per Tipo Veicolo */}
            <div>
              <label htmlFor="tipologia_veicolo_filter" className="block text-sm font-medium text-gray-700">Tipo Veicolo</label>
              <div className="relative mt-1">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FaCar className="text-gray-400" />
                </span>
                <select
                  id="tipologia_veicolo_filter"
                  name="tipologia_veicolo"
                  value={filters.tipologia_veicolo}
                  onChange={handleFilterChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mioblu focus:border-mioblu sm:text-sm appearance-none bg-white"
                >
                  <option value="">Tutti</option>
                  {tipologiaVeicoloOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Nuovo filtro per Agente Inseritore */}
            <div>
              <label htmlFor="nome_agente_filter" className="block text-sm font-medium text-gray-700">Account Inseritore</label>
              <div className="relative mt-1">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3"><FaUser className="text-gray-400" /></span>
                <select
                  id="nome_agente_filter"
                  name="nome_agente"
                  value={filters.nome_agente}
                  onChange={handleFilterChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mioblu focus:border-mioblu sm:text-sm appearance-none bg-white"
                >
                  <option value="">Tutti gli Account</option>
                  {agenteOptions.map(agent => (
                    <option key={agent.id} value={agent.id}>{agent.nome} {agent.cognome}</option>
                  ))}
                </select>
              </div>
            </div>

          </div>
          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={handleClearFilters}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors flex items-center"
            >
              <FaTimes className="mr-2" /> Cancella Filtri
            </button>
          </div>
        </div>

        {/* Tabella Adesioni */}
        <div className="overflow-x-auto rounded-lg shadow-md border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 table-auto">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  N° Adesione
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agente Account
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipologia
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CF / PI
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo Veicolo
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Modello Auto
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Imm.
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Targa
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Inizio Decorrenza
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fine Decorrenza
                </th>
                {(activeTab === 'attive' || activeTab === 'scadute') && (
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pacchetto
                  </th>
                )}
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valore Adesione
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valore Netto
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stato
                </th>
                {(activeTab === 'attive' || activeTab === 'scadute') && (
  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
    Data Creazione
  </th>
)}

{activeTab === 'eliminate' && (
  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
    Data Eliminazione
  </th>
)}

{activeTab === 'annullate' && (
  <>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Data Risoluzione
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Premio Restituito
    </th>
  </>
)}
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[16rem]">
                  Note
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {policies.length === 0 ? (
                <tr>
                  <td colSpan={totalCols} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                    {isLoading ? 'Caricamento...' : 'Nessuna adesione trovata con i criteri selezionati.'}
                  </td>
                </tr>
              ) : (
                policies.map((policy) => {
                  let minEditInizioDecorrenza = '';
                  let maxEditInizioDecorrenza = '';
                  let minEditFineDecorrenza = '';
                  let maxEditFineDecorrenza = '';

                  const today = new Date().toLocaleDateString('fr-CA', { timeZone: 'Europe/Rome' }); // '2025-07-17'
minEditInizioDecorrenza = today;
                  const tempMaxInizio = new Date(today);
                  tempMaxInizio.setMonth(tempMaxInizio.getMonth() + 4);
                  maxEditInizioDecorrenza = tempMaxInizio.toISOString().split('T')[0];

                  if (editedPolicyData?.inizio_decorrenza) {
                    const editStartDate = new Date(editedPolicyData.inizio_decorrenza);
                    const tempMinFine = new Date(editStartDate);
                    tempMinFine.setMonth(tempMinFine.getMonth() + 2);
                    if (tempMinFine <= editStartDate) {
                      tempMinFine.setDate(editStartDate.getDate() + 1);
                    }
                    minEditFineDecorrenza = tempMinFine.toISOString().split('T')[0];

                    const tempMaxFine = new Date(editStartDate);
                    tempMaxFine.setFullYear(tempMaxFine.getFullYear() + 1); // Aggiungi un anno
                    tempMaxFine.setMonth(tempMaxFine.getMonth() + 1);    // Aggiungi un mese
                    maxEditFineDecorrenza = tempMaxFine.toISOString().split('T')[0];
                  }

                  return (
                    <tr key={policy.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {policy.numero_polizza || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {policy.agenti?.nome} {policy.agenti?.cognome}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {policy.cliente_privato ? 'P' : 'A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {policy.cliente_privato ? `${policy.nome_cliente || ''} ${policy.cognome_cliente || ''}` : policy.ragione_sociale || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {policy.cliente_privato ? (policy.codice_fiscale || 'N/A') : (policy.partita_iva || 'N/A')}
                      </td>
                      {editingPolicyId === policy.id && activeTab === 'attive' ? (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <select
                              name="tipologia_veicolo"
                              value={editedPolicyData?.tipologia_veicolo || ''}
                              onChange={handleEditChange}
                              className="w-36 border border-gray-300 rounded-md px-2 py-1"
                            >
                              {tipologiaVeicoloOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <input
                              type="text"
                              name="modello_auto"
                              value={editedPolicyData?.modello_auto || ''}
                              onChange={handleEditChange}
                              className="w-28 border border-gray-300 rounded-md px-2 py-1"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <input
                              type="date"
                              name="data_immatricolazione"
                              value={editedPolicyData?.data_immatricolazione || ''}
                              onChange={handleEditChange}
                              className="w-32 border border-gray-300 rounded-md px-2 py-1"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <input
                              type="text"
                              name="targa"
                              value={editedPolicyData?.targa || ''}
                              onChange={handleEditChange}
                              maxLength="10"
                              className="w-28 border border-gray-300 rounded-md px-2 py-1 uppercase"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <input
                              type="date"
                              name="inizio_decorrenza"
                              value={editedPolicyData?.inizio_decorrenza || ''}
                              onChange={handleEditChange}
                              min={minEditInizioDecorrenza}
                              max={maxEditInizioDecorrenza}
                              className="w-32 border border-gray-300 rounded-md px-2 py-1"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <input
                              type="date"
                              name="fine_decorrenza"
                              value={editedPolicyData?.fine_decorrenza || ''}
                              onChange={handleEditChange}
                              disabled={!editedPolicyData?.inizio_decorrenza}
                              min={minEditFineDecorrenza}
                              max={maxEditFineDecorrenza}
                              className="w-32 border border-gray-300 rounded-md px-2 py-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <select
                              name="id_pacchetto_garanzia"
                              value={editedPolicyData?.id_pacchetto_garanzia || ''}
                              onChange={handleEditChange}
                              className="w-36 border border-gray-300 rounded-md px-2 py-1"
                            >
                              {pacchettiGaranziaOptions.map(pkg => (
                                <option key={pkg.id} value={pkg.id}>{pkg.nome}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <input
                              type="text"
                              name="valore_polizza_calcolato"
                              value={`€ ${parseFloat(editedPolicyData?.valore_polizza_calcolato || 0).toFixed(2)}`}
                              readOnly
                              disabled
                              className="w-28 border border-gray-300 rounded-md px-2 py-1 bg-gray-100 cursor-not-allowed"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
{(() => {
const selectedPkg = pacchettiGaranziaOptions.find(p => p.id === editedPolicyData?.id_pacchetto_garanzia);
const perc = selectedPkg?.percentuale_netto ?? policy?.pacchetti_garanzia?.percentuale_netto ?? 1;
const lordo = parseFloat(editedPolicyData?.valore_polizza_calcolato || 0);
const netto = (lordo * perc).toFixed(2);
return (
 <input
type="text"
value={`€ ${netto}`}
readOnly
disabled
className="w-28 border border-gray-300 rounded-md px-2 py-1 bg-gray-100 cursor-not-allowed"
 />
 );
 })()}
</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <select
                              name="stato_polizza"
                              value={editedPolicyData?.stato_polizza || ''}
                              onChange={handleEditChange}
                              className="w-28 border border-gray-300 rounded-md px-2 py-1"
                            >
                               {statoPolizzaOptions.map(stato => (
                                 <option key={stato.value} value={stato.value}>{stato.label}</option>
                               ))}
                            </select>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(policy.created_at).toLocaleDateString('it-IT', { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="px-6 py-4 whitespace-normal text-sm min-w-[16rem]">
                            <textarea
                              name="note"
                              value={editedPolicyData?.note || ''}
                              onChange={handleEditChange}
                              rows="3"
                              className="w-full border border-gray-300 rounded-md px-2 py-1"
                            ></textarea>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end items-center gap-2">
                              <button
                                onClick={() => handleSaveEdit(policy.id)}
                                className="w-9 h-9 flex items-center justify-center p-0 text-base bg-green-500 text-white rounded transition-colors duration-200 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500"
                                title="Salva Modifiche"
                                disabled={isLoading}
                              >
                                <FaSave />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="w-9 h-9 flex items-center justify-center p-0 text-base bg-gray-500 text-white rounded transition-colors duration-200 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                title="Annulla Modifiche"
                                disabled={isLoading}
                              >
                                <FaBan />
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {policy.tipologia_veicolo}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {policy.modello_auto || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {policy.data_immatricolazione || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {policy.targa}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {policy.inizio_decorrenza}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {policy.fine_decorrenza}
                          </td>
                          {activeTab === 'attive' && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {policy.pacchetti_garanzia?.nome || 'N/A'}
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            € {parseFloat(policy.valore_polizza_calcolato || 0).toFixed(2)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
{(() => {
const lordo = parseFloat(policy.valore_polizza_calcolato || 0);
const perc = policy.pacchetti_garanzia?.percentuale_netto ?? 1;
const netto = (lordo * perc).toFixed(2);
return `€ ${netto}`;
 })()}
</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                              ${policy.stato_polizza === 'ATTIVA' ? 'bg-green-100 text-green-800' : ''}
                              ${policy.stato_polizza === 'SCADUTA' ? 'bg-red-100 text-red-800' : ''}
                              ${policy.stato_polizza === 'ELIMINATA' ? 'bg-gray-100 text-gray-800' : ''}
                              ${policy.stato_polizza === 'ANNULLATA' ? 'bg-yellow-100 text-yellow-800' : ''}
                            `}>
                              {policy.stato_polizza}
                            </span>
                          </td>
                          {(activeTab === 'attive' || activeTab === 'scadute') && (
  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
    {new Date(policy.created_at).toLocaleDateString('it-IT')}
  </td>
)}

{activeTab === 'eliminate' && (
  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
    {new Date(policy.deleted_at).toLocaleDateString('it-IT')}
  </td>
)}

{activeTab === 'annullate' && (
  <>
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
      {policy.data_risoluzione
        ? new Date(policy.data_risoluzione).toLocaleDateString('it-IT')
        : 'N/A'}
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
      {policy.premio_restituito
        ? `${parseFloat(policy.premio_restituito).toFixed(2)} €`
        : '0,00 €'}
    </td>
  </>
)}
                          <td className="px-6 py-4 whitespace-normal text-sm text-gray-900 min-w-[16rem]">
                              {policy.note || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end items-center gap-2">
                              {activeTab === 'attive' && canEditPolicy(policy.created_at) ? (
                                <button
                                  onClick={() => handleEditClick(policy)}
                                  className="w-9 h-9 flex items-center justify-center p-0 text-base bg-blue-500 text-white rounded transition-colors duration-200 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  title="Modifica Adesione"
                                >
                                  <FaEdit />
                                  </button>
                              ) : (
                                activeTab === 'attive' && (
                                  <button
                                    onClick={() => handleDeletePolicy(policy.id, policy.numero_polizza)}
                                    className="w-9 h-9 flex items-center justify-center p-0 text-base bg-red-500 text-white rounded transition-colors duration-200 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                                    title="Elimina Adesione Definitivamente"
                                  >
                                    <FaTrash />
                                  </button>
                                )
                              )}
                              {activeTab === 'attive' && (
                                <button
                                  onClick={() => handleDownloadPdf(policy)}
                                  className="w-9 h-9 flex items-center justify-center p-0 text-base bg-gray-500 text-white rounded transition-colors duration-200 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                  title="Scarica PDF"
                                >
                                  <FaFilePdf />
                                </button>
                              )}
                              {activeTab === 'attive' && (
  <button
    onClick={() => navigate(`/dashboard/agenzia/annulla-polizza?id=${policy.id}`)}
    className="w-9 h-9 flex items-center justify-center p-0 text-base bg-yellow-600 text-white rounded transition-colors duration-200 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-gray-500" 
    title="Annulla Adesione"
  >
     <FaFileInvoiceDollar />
  </button>
)}

{activeTab === 'eliminate' && (
  <button
    onClick={() => handleDownloadAnnullamentoPdf(policy.id)}
    className="w-9 h-9 flex items-center justify-center p-0 text-base bg-blue-500 text-white rounded transition-colors duration-200 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
    title="Scarica PDF Annullamento"
  >
    <FaFilePdf />
  </button>
)}
{activeTab === 'annullate' && (
  <button
    onClick={() => handleDownloadRisoluzionePdf(policy.id)}
    className="w-9 h-9 flex items-center justify-center p-0 text-base bg-blue-500 text-white rounded transition-colors duration-200 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
    title="Scarica PDF Risoluzione"
  >
    <FaFilePdf />
  </button>
)}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          <div className="flex justify-center items-center mt-4 mb-4 gap-2">
  <button
    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
    disabled={currentPage === 1}
    className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
  >
    ← Precedente
  </button>

  <span>
    Pagina {currentPage} di {Math.max(1, Math.ceil(totalRows / rowsPerPage))}
  </span>

  <button
    onClick={() => setCurrentPage(p => (p < Math.ceil(totalRows / rowsPerPage) ? p + 1 : p))}
    disabled={currentPage >= Math.ceil(totalRows / rowsPerPage)}
    className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
  >
    Successiva →
  </button>
</div>
        </div>
      </div>

      {/* Modale di Conferma (ora condizionale per i bottoni) */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full text-center">
            <h3 className="text-xl font-bold mb-4 text-gray-800">{modalTitle}</h3>
            <p className="text-gray-700 whitespace-pre-line mb-6">{modalMessage}</p>
            <div className="flex flex-row items-center justify-center gap-4">
              {isDecisionModal ? (
                <>
                  <button
                    className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                    onClick={() => {
                      if (modalAction) {
                        modalAction();
                      }
                      closeConfirmModal();
                    }}
                  >
                    Sì
                  </button>
                  <button
                    className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors"
                    onClick={closeConfirmModal}
                  >
                    No
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="px-6 py-2 bg-miorosso text-white rounded-md hover:bg-miorossohover transition-colors"
                    onClick={() => {
                      if (modalAction) {
                        modalAction();
                      }
                      closeConfirmModal();
                    }}
                  >
                    OK
                  </button>
{downloadAnnullamentoId && (
  <button
    onClick={() => handleDownloadAnnullamentoPdf(downloadAnnullamentoId)}
    className="inline-flex items-center px-6 py-2 bg-blue-500 text-white rounded-md
              hover:bg-blue-600 transition-colors"
  >
    <FaFilePdf className="w-4 h-4 mr-2" />
    Scarica PDF
  </button>
)}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
